import {
  call,
  put,
  takeEvery,
  takeLatest,
  cancel,
  select,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { initialize } from 'redux-form';
import moment from 'moment';

import {
  REPORTS_INVOICES_PAGE_REQUEST,
  REPORTS_INVOICES_LIST_INVOICE_REQUEST,
  REPORTS_INVOICES_ON_REACH_BOTTOM,
} from '../../actions/reports/invoicesv2/constants';
import { LOGOUT } from '../../actions/constants';
import {
  listInvoiceRequest,
  setListInvoice,
  setOutlets,
  setNextCursor,
  setHasMore,
} from '../../actions/reports/invoicesv2';
import { setReceiptInvoices } from '../../actions/reports/invoices';
import * as actActionBar from '../../actions/actionBar';
import * as actNotif from '../../actions/notifications';
import * as act from '../../actions';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';

import * as apiOrderReporting from '../../api/orderReporting';
import * as apiOutlets from '../../api/outlets';
import { DEFAULT_START_HOUR, DEFAULT_END_HOUR } from '../../utils/hourFilter';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapReportsInvoices from '../../clevertap/reports/invoices';
import { getFilteredDateAsMoment } from '../api/get-filtered-date';

function closeRightColumn() {
  const toggledClass = 'col-sm-12 col-md-6 col-xs-6';
  $('#mk-col-left, #mk-col-right').removeClass(toggledClass);
  $('#mk-col-left').addClass('col-sm-12');
}

function getStatus(statuses) {
  return statuses ? statuses.toString() : statuses;
}

function getOutletName(outlets, outletId) {
  return outlets.find((outlet) => outlet.id === outletId).moka.name;
}

function formatDate(
  { startDate, endDate, startHour, endHour, allDay },
  dateFormat
) {
  let startTime = `${moment(startDate).format(`${dateFormat}00:00:00`)}`;
  let endTime = `${moment(endDate).format(`${dateFormat}23:59:59`)}`;

  if (!allDay) {
    // eslint-disable-next-line no-param-reassign
    startHour = startHour || '00:00';
    // eslint-disable-next-line no-param-reassign
    endHour = endHour || '23:59';

    startTime = `${moment(startDate).format(`${dateFormat}${startHour}:00`)}`;
    endTime = `${moment(endDate).format(`${dateFormat}${endHour}:00`)}`;
  }

  return { startTime, endTime };
}

function* getQueryParam(searchValues) {
  const { date, statuses, keyword, pageRequestFlow } = searchValues;

  const nextCursor = pageRequestFlow
    ? ''
    : yield select((state: any) => state.reports.invoicesv2.nextCursor);

  const dateFormat = 'YYYY-MM-DDT';
  const queryDates = formatDate(date, dateFormat);

  const queryParam = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    start_time: queryDates.startTime,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    end_time: queryDates.endTime,
    keyword,
    statuses: getStatus(statuses),
    cursor: nextCursor,
  };
  return queryParam;
}

function getHeaders(searchValues) {
  const { outletIds } = searchValues;
  const headers = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    outlet_id: outletIds.length > 1 ? '' : outletIds[0],
  };
  return headers;
}

function* getOrderReportingInvoices(searchValues) {
  yield put(act.sendingRequest(true));
  yield put(act.showProgressBar());

  try {
    const queryParams = yield call(getQueryParam, searchValues);
    const header = yield call(getHeaders, searchValues);
    const response = yield call(
      apiOrderReporting.getInvoices,
      queryParams,
      header
    );
    return response.data;
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
    return false;
  } finally {
    yield put(act.hideProgressBar());
    yield put(act.sendingRequest(false));
  }
}

function* initForm(outletIds, date) {
  const { startDate, endDate } = date;
  const allDay = moment(endDate).format('HH:mm') === '23:59';
  const startHour = !allDay
    ? moment(startDate).format('HH:mm')
    : DEFAULT_START_HOUR;
  const endHour = !allDay ? moment(endDate).format('HH:mm') : DEFAULT_END_HOUR;

  yield put(
    initialize('search', {
      outlet: outletIds,
      date: { startDate, endDate, startHour, endHour, allDay },
    })
  );
}

export function* invoicesPageRequestFlow() {
  try {
    yield put(setListInvoice(null));
    yield put(showPageLoadingSpinner(true));
    // remove any previous receipt invoice
    yield put(setReceiptInvoices(null));
    // hide receipt in first load page
    yield call(closeRightColumn);

    const date = yield call(getFilteredDateAsMoment);
    const resOutlets = yield call(apiOutlets.outlets);
    yield put(setOutlets(resOutlets.data));
    yield put(actActionBar.setOutlets(resOutlets.data));
    const outletIds = getSelectedOutletIdFromLocalStorage();
    yield call(initForm, outletIds, date);

    const { startDate, endDate } = date;
    const pageRequestFlow = true;

    yield put(
      listInvoiceRequest({
        date: yield select((state: any) => state.form.search.values.date),
        outletIds,
        pageRequestFlow,
      })
    );
    clevertapReportsInvoices.reportsInvoiceStart({
      outletIds,
      startDate,
      endDate,
    });
    $('#invoice-table-tbody').animate({ scrollTop: 0 }, 'slow');
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(actNotif.error(error.message));
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* getInvoicesFlow(request) {
  const {
    payload: { searchValues, previousInvoices },
  } = request;

  yield put(setReceiptInvoices(null));
  yield call(closeRightColumn);

  if (searchValues.onChangeKeyword) {
    yield delay(300);
  }
  setSelectedOutletIdToLocalStorage(searchValues.outletIds);
  const outlets = yield select(
    (state: any) => state.reports.invoicesv2.outlets
  );

  const resInvoices = yield call(getOrderReportingInvoices, searchValues);

  if (!previousInvoices) yield put(setListInvoice(null));
  if (resInvoices) {
    let invoices;
    if (previousInvoices) {
      invoices = previousInvoices.concat(resInvoices.invoices);
    } else {
      invoices = resInvoices.invoices;
    }

    invoices.forEach((invoice) => {
      invoice.outlet_name = getOutletName(outlets, invoice.outlet_id);
    });

    const nextCursor = resInvoices.next_cursor;
    yield put(setListInvoice(invoices));
    yield put(setNextCursor(nextCursor));
    yield put(setHasMore(!!nextCursor));
  }
  yield put(showPageLoadingSpinner(false));
}

export function* onReachBottom(params) {
  yield delay(100);
  yield put(
    listInvoiceRequest(
      params.payload.searchValues,
      params.payload.previousInvoices
    )
  );
}

export function* cancelAction(cancelledTasks) {
  yield cancel(...cancelledTasks);
}

export default function* reportsInvoicesV2Saga() {
  const pageReqTask = yield takeLatest(
    REPORTS_INVOICES_PAGE_REQUEST,
    invoicesPageRequestFlow
  );
  const getInvoicesTask = yield takeLatest(
    REPORTS_INVOICES_LIST_INVOICE_REQUEST,
    getInvoicesFlow
  );
  yield takeLatest(REPORTS_INVOICES_ON_REACH_BOTTOM, onReachBottom);
  yield takeEvery(LOGOUT, cancelAction, [pageReqTask, getInvoicesTask]);
}
